import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Form, Button, Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Chart, Tooltip, Legend } from 'chart.js';
import { createLogger } from '../utils/debugLogger';
import { ThemeContext } from '../contexts/ThemeContext';
import './TreeMapModal.css';

const logger = createLogger('treeMapModal', 'Tree map visualization modal for portfolio breakdown', true, 'DEBUG');

// Register Chart.js components
Chart.register(Tooltip, Legend);

function TreeMapModal({
  show,
  onHide,
  tickers,
  cash,
  lastRefreshTime: _lastRefreshTime,
  refreshInterval,
  autoRefreshEnabled,
  onRefresh,
  marketInfo: _marketInfo,
  previousBalance = null,
  inline = false,
  storageKeyPrefix = '',
  hideControls = false,
  hideLegend = false,
  hideTitle = false,
  hidePreBal = false,
  externalViewMode = null,
  onViewModeChange = null,
  externalGainBasis = null,
  onGainBasisChange = null,
  fontSizeMultiplier = 1.0
}) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const navigate = useNavigate();
  const { effectiveTheme } = useContext(ThemeContext);
  const isDark = effectiveTheme === 'dark';
  const [internalUseToday, setInternalUseToday] = useState(() => {
    const stored = localStorage.getItem(`${storageKeyPrefix}treemapGainBasis`);
    if (stored === 'today' || stored === 'total') {
      return stored === 'today';
    }
    return true;
  });
  const [selectedCategoryLabel, setSelectedCategoryLabel] = useState(null);
  const [internalViewMode, setInternalViewMode] = useState(() => {
    const stored = localStorage.getItem(`${storageKeyPrefix}treemapViewMode`);
    return stored === 'ticker' ? 'ticker' : 'category';
  });

  // Use external gain basis if provided, otherwise use internal state
  const useToday = externalGainBasis ? (externalGainBasis === 'today') : internalUseToday;

  // Use external view mode if provided, otherwise use internal state
  const viewMode = externalViewMode || internalViewMode;

  const treemapPalette = useMemo(() => ({
    gain: [
      { threshold: 3, color: isDark ? '#1f3c2b' : 'rgb(144, 238, 144)' },
      { threshold: 5, color: isDark ? '#2b5740' : 'rgb(60, 179, 113)' },
      { threshold: 9, color: isDark ? '#3a7452' : 'rgb(0, 128, 0)' },
      { threshold: Infinity, color: isDark ? '#4f9365' : 'rgb(0, 100, 0)' }
    ],
    loss: [
      { threshold: 3, color: isDark ? '#3b1f24' : 'rgb(255, 182, 193)' },
      { threshold: 5, color: isDark ? '#5a252b' : 'rgb(220, 20, 60)' },
      { threshold: 9, color: isDark ? '#7a2b31' : 'rgb(178, 34, 34)' },
      { threshold: Infinity, color: isDark ? '#9b343b' : 'rgb(139, 0, 0)' }
    ],
    neutral: isDark ? '#1c212a' : '#f8f9fa',
    border: isDark ? 'rgba(148, 163, 184, 0.35)' : 'white',
    textLight: '#f8fafc',
    textDark: '#0f172a',
    background: isDark ? '#0f1319' : 'white',
    shadowLight: 'rgba(0, 0, 0, 0.35)',
    shadowDark: 'rgba(255, 255, 255, 0.5)'
  }), [isDark]);

  const parseColor = useCallback((color) => {
    if (!color) return null;
    const trimmed = color.trim();
    if (trimmed.startsWith('#')) {
      const hex = trimmed.replace('#', '');
      if (hex.length === 3) {
        const r = parseInt(hex[0] + hex[0], 16);
        const g = parseInt(hex[1] + hex[1], 16);
        const b = parseInt(hex[2] + hex[2], 16);
        return { r, g, b };
      }
      if (hex.length === 6) {
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return { r, g, b };
      }
      return null;
    }
    const rgbMatch = trimmed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (rgbMatch) {
      return {
        r: parseInt(rgbMatch[1], 10),
        g: parseInt(rgbMatch[2], 10),
        b: parseInt(rgbMatch[3], 10)
      };
    }
    return null;
  }, []);

  const getTextToneForFill = useCallback((fill) => {
    const rgb = parseColor(fill);
    if (!rgb) return treemapPalette.textDark;
    const luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
    return luminance < 0.55 ? treemapPalette.textLight : treemapPalette.textDark;
  }, [parseColor, treemapPalette]);

  const selectedCategory = useMemo(() => {
    if (!selectedCategoryLabel || !Array.isArray(tickers) || tickers.length === 0) {
      return null;
    }

    const categoryTickers = tickers.filter((ticker) => {
      if (!ticker || ticker.current_value <= 0) return false;
      const categoryName = ticker.category?.name || 'Uncategorized';
      return categoryName === selectedCategoryLabel;
    });

    if (categoryTickers.length === 0) {
      return null;
    }

    const categoryValue = categoryTickers.reduce((sum, ticker) => sum + (ticker.current_value || 0), 0);
    const todayGain = categoryTickers.reduce((sum, ticker) => sum + (ticker.today_gain_amount || 0), 0);
    const totalGain = categoryTickers.reduce((sum, ticker) => sum + (ticker.total_gain || 0), 0);

    return {
      label: selectedCategoryLabel,
      category: {
        tickers: categoryTickers,
        value: categoryValue,
        todayGain,
        totalGain
      }
    };
  }, [selectedCategoryLabel, tickers]);

  // Wrapper to handle gain basis changes
  const handleGainBasisChange = (newUseToday) => {
    if (onGainBasisChange) {
      onGainBasisChange(newUseToday ? 'today' : 'total');
    } else {
      setInternalUseToday(newUseToday);
    }
  };

  // Wrapper to handle view mode changes
  const handleViewModeChange = (newMode) => {
    if (onViewModeChange) {
      onViewModeChange(newMode);
    } else {
      setInternalViewMode(newMode);
    }
  };

  // Persist preferences
  useEffect(() => {
    // Only persist if not externally controlled
    if (!externalGainBasis) {
      localStorage.setItem(`${storageKeyPrefix}treemapGainBasis`, internalUseToday ? 'today' : 'total');
    }
  }, [internalUseToday, storageKeyPrefix, externalGainBasis]);

  useEffect(() => {
    // Only persist if not externally controlled
    if (!externalViewMode) {
      localStorage.setItem(`${storageKeyPrefix}treemapViewMode`, internalViewMode);
    }
  }, [internalViewMode, storageKeyPrefix, externalViewMode]);

  // Clean up chart instance when component unmounts
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, []);

  // Setup auto-refresh for the treemap
  useEffect(() => {
    // Only set up the interval if the modal is showing and auto-refresh is enabled
    if (!show || !autoRefreshEnabled || !refreshInterval) return;
    
    const intervalId = setInterval(() => {
      logger.debug(`TreeMap auto-refreshing with interval: ${refreshInterval} seconds`);
      if (onRefresh) {
        onRefresh();
      }
    }, refreshInterval * 1000);
    
    // Clean up interval when component unmounts or dependencies change
    return () => {
      clearInterval(intervalId);
    };
  }, [show, autoRefreshEnabled, refreshInterval, onRefresh]);

  // Go back to main treemap
  const handleBackToMain = () => {
    setSelectedCategoryLabel(null);
  };

  // Switching view mode should reset category drill-downs
  useEffect(() => {
    if (viewMode === 'ticker') {
      setSelectedCategoryLabel(null);
    }
  }, [viewMode]);

  useEffect(() => {
    if (selectedCategoryLabel && !selectedCategory) {
      setSelectedCategoryLabel(null);
    }
  }, [selectedCategoryLabel, selectedCategory]);

  // Create or update chart when modal is shown or data changes
  useEffect(() => {
    if (!show || !chartRef.current) return;

    logger.debug('Recreating treemap');
    
    // Clean up previous listener to avoid duplicates
    const canvas = chartRef.current;
    if (canvas) {
      canvas.removeEventListener('click', handleCanvasClick);
    }
    
    // Destroy previous chart instance if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Get the canvas context
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size with high resolution for retina displays
    const container = canvas.parentElement;
    const rect = container.getBoundingClientRect();
    const pixelRatio = window.devicePixelRatio || 1;
    
    // Set the canvas width/height with physical pixels
    canvas.width = rect.width * pixelRatio;
    canvas.height = rect.height * pixelRatio;
    
    // Scale all drawing operations by the pixelRatio
    ctx.scale(pixelRatio, pixelRatio);
    
    // Set the CSS size of the canvas
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    
    // Clear the canvas
    ctx.clearRect(0, 0, rect.width, rect.height);
    
    // Prepare data
    const items = [];

    // Basis used to convert a ticker's dollar gain into a percentage when its
    // own precomputed percentage field isn't available: previous-close value
    // for today's gain, cost basis for total gain -- mirroring how the
    // backend computes today_gain_percentage / total_gain_pct. Deliberately
    // NOT current market value: current value drifts away from both bases
    // (price moves intraday, shares change on buys/sells), so dividing by it
    // silently produces a wildly wrong percentage instead of a merely-stale
    // one. Shared by the ticker-view, category-drilldown, and category-rollup
    // branches below so a fix here can't drift out of sync between them.
    const getTickerGainBasis = (ticker, useTodayBasis) => {
      if (useTodayBasis) {
        const prevValue = (ticker.current_value || 0) - (ticker.today_gain_amount || 0);
        return prevValue > 0 ? prevValue : null;
      }
      const totalCost = ticker.total_cost ??
        (ticker.cost_basis && ticker.shares ? ticker.cost_basis * ticker.shares : null);
      return (totalCost && totalCost > 0) ? totalCost : null;
    };

    const getTickerGainAmount = (ticker, useTodayBasis) =>
      useTodayBasis ? (ticker.today_gain_amount || 0) : (ticker.total_gain || 0);

    const getTickerGainPct = (ticker, useTodayBasis) => {
      const precomputed = useTodayBasis
        ? (ticker.today_gain_percentage ?? ticker.today_gain_percent)
        : (ticker.total_gain_pct ?? ticker.total_gain_percent);
      if (precomputed !== undefined && precomputed !== null && !isNaN(precomputed)) {
        return precomputed;
      }
      const basis = getTickerGainBasis(ticker, useTodayBasis);
      return basis ? (getTickerGainAmount(ticker, useTodayBasis) / basis) * 100 : 0;
    };

    const getColorForGain = (gainValue, gainPct) => {
      if (gainValue > 0) {
        if (gainPct < treemapPalette.gain[0].threshold) return treemapPalette.gain[0].color;
        if (gainPct < treemapPalette.gain[1].threshold) return treemapPalette.gain[1].color;
        if (gainPct < treemapPalette.gain[2].threshold) return treemapPalette.gain[2].color;
        return treemapPalette.gain[3].color;
      }
      if (gainValue < 0) {
        const lossPct = Math.abs(gainPct);
        if (lossPct < treemapPalette.loss[0].threshold) return treemapPalette.loss[0].color;
        if (lossPct < treemapPalette.loss[1].threshold) return treemapPalette.loss[1].color;
        if (lossPct < treemapPalette.loss[2].threshold) return treemapPalette.loss[2].color;
        return treemapPalette.loss[3].color;
      }
      return treemapPalette.neutral;
    };
    
    // Check if we're showing a subcategory view
    if (viewMode === 'ticker') {
      // Add cash as a data point
      if (cash && cash > 0) {
        items.push({
          label: 'Cash',
          value: cash,
          color: treemapPalette.neutral
        });
      }

      tickers?.forEach(ticker => {
        if (!ticker || ticker.current_value <= 0) return;

        const gainValue = getTickerGainAmount(ticker, useToday);
        const gainPct = getTickerGainPct(ticker, useToday);

        items.push({
          label: ticker.symbol,
          value: ticker.current_value,
          color: getColorForGain(gainValue, gainPct),
          ticker: ticker,
          isSingleTickerView: true,
          gainPct: gainPct
        });
      });
    } else if (selectedCategory && selectedCategory.category) {
      // Prepare ticker items for subcategory view
      selectedCategory.category.tickers.forEach(ticker => {
        if (ticker && ticker.current_value > 0) {
          // Determine color based on gain/loss
          // Choose which gain to use based on toggle
          const gainValue = getTickerGainAmount(ticker, useToday);
          const gainPct = getTickerGainPct(ticker, useToday);

          logger.debug(`Ticker ${ticker.symbol}: current_value=${ticker.current_value}, ${useToday ? 'today_gain' : 'total_gain'}=${gainValue}, calculated gainPct=${gainPct}`);
          
          items.push({
            label: ticker.symbol,
            value: ticker.current_value,
            color: getColorForGain(gainValue, gainPct),
            ticker: ticker,
            isSingleTickerView: true,
            gainPct: gainPct  // Store the selected gain percentage
          });
        }
      });
    } else {
      // Main treemap view - same as before
      // Add cash as a data point
      if (cash && cash > 0) {
        items.push({
          label: 'Cash',
          value: cash,
          color: treemapPalette.neutral,
        });
      }
      
      // Object to store category details including tickers
      const categoryDetails = {};

      const emptyCategory = () => ({
        tickers: [],
        value: 0,
        // Value-weighted sum of each member's OWN gain% (see below for why
        // this doesn't touch today_gain_amount/total_gain directly).
        weightedPctSum: 0
      });

      // Initialize uncategorized
      categoryDetails['Uncategorized'] = emptyCategory();

      // Group tickers by category
      if (tickers && tickers.length > 0) {
        // Process all tickers and group by category
        tickers.forEach((ticker) => {
          if (ticker && ticker.current_value > 0) {
            // Check if ticker has a category
            const categoryName = (ticker.category && ticker.category.name) || 'Uncategorized';

            // Initialize category if it doesn't exist yet
            if (!categoryDetails[categoryName]) {
              categoryDetails[categoryName] = emptyCategory();
            }

            const bucket = categoryDetails[categoryName];
            // getTickerGainPct() prefers the ticker's own precomputed
            // percentage field (what the ticker table shows) over deriving
            // one from today_gain_amount/total_gain. That distinction turned
            // out to matter: those dollar fields can go stale/inflated on
            // their own (observed live: today_gain_amount reading ~9x too
            // high while today_gain_percentage stayed correct on the same
            // ticker), so a rollup built from them inherits the corruption
            // even when the math combining them is otherwise correct. Value-
            // weighting by current_value -- rather than by a basis derived
            // from those same dollar fields -- avoids leaning on them at all.
            const pct = getTickerGainPct(ticker, useToday);

            bucket.tickers.push(ticker);
            bucket.value += ticker.current_value;
            bucket.weightedPctSum += ticker.current_value * pct;
          }
        });

        // Convert categories to items for the treemap
        Object.entries(categoryDetails).forEach(([categoryName, category]) => {
          if (category.value > 0) {
            const gainPct = category.weightedPctSum / category.value;

            // Permanent regression guard: a category's gain% should never
            // fall outside the range spanned by its own members' gain%,
            // since it's now literally a weighted average of them. If this
            // ever fires, the invariant broke somewhere -- dump everything
            // needed to see why instead of guessing.
            const memberPcts = category.tickers.map(t => getTickerGainPct(t, useToday));
            const minPct = Math.min(...memberPcts);
            const maxPct = Math.max(...memberPcts);
            const tolerance = 0.5; // percentage points, allow for rounding
            if (gainPct < minPct - tolerance || gainPct > maxPct + tolerance) {
              // eslint-disable-next-line no-console
              console.warn('[TreeMap anomaly] category gain% outside member range', {
                category: categoryName,
                useToday,
                computedGainPct: gainPct,
                memberRange: [minPct, maxPct],
                memberCount: category.tickers.length,
                categoryValue: category.value,
                members: category.tickers.map(t => ({
                  ticker_id: t.ticker_id,
                  symbol: t.symbol,
                  current_value: t.current_value,
                  shares: t.shares,
                  today_gain_amount: t.today_gain_amount,
                  today_gain_percentage: t.today_gain_percentage,
                  total_gain: t.total_gain,
                  total_gain_pct: t.total_gain_pct,
                  cost_basis: t.cost_basis,
                  total_cost: t.total_cost
                }))
              });
            }

            items.push({
              label: categoryName,
              value: category.value,
              color: getColorForGain(gainPct, gainPct),
              category: category,
              gainPct: gainPct
            });
          }
        });
      }
    }
    
    // Calculate total value
    const totalValue = items.reduce((sum, item) => sum + item.value, 0);
    
    // If no data, show a message
    if (items.length === 0 || totalValue === 0) {
      ctx.font = '16px Arial';
      ctx.fillStyle = isDark ? treemapPalette.textLight : treemapPalette.textDark;
      ctx.textAlign = 'center';
      ctx.fillText('No data available for TreeMap', rect.width / 2, rect.height / 2);
      return;
    }
    
    // Update items to include percentage data for all items including cash
    items.forEach(item => {
      item.percentage = (item.value / totalValue) * 100;
    });
    
    // Sort items by value (largest first)
    items.sort((a, b) => b.value - a.value);
    
    // Canvas dimensions (for drawing, use logical CSS pixels)
    const canvasWidth = rect.width;
    const canvasHeight = rect.height;
    const padding = 5;
    
    // Create a simple treemap algorithm
    function createTreeMap(items, x, y, width, height) {
      // Base case
      if (items.length === 0) return;
      if (items.length === 1) {
        const item = items[0];
        drawRect(item, x, y, width, height);
        return;
      }
      
      // Calculate sum
      const sum = items.reduce((acc, item) => acc + item.value, 0);
      
      // Decide direction (horizontal or vertical split)
      const isHorizontal = width >= height;
      
      let currentSum = 0;
      let splitIndex = 0;
      
      // Find the best split point that's closest to half
      for (let i = 0; i < items.length; i++) {
        currentSum += items[i].value;
        if (currentSum >= sum / 2) {
          splitIndex = i;
          break;
        }
      }
      
      // Split into two groups
      const group1 = items.slice(0, splitIndex + 1);
      const group2 = items.slice(splitIndex + 1);
      
      // Calculate ratio for the split
      const ratio = currentSum / sum;
      
      if (isHorizontal) {
        // Split horizontally
        const width1 = Math.floor(width * ratio);
        createTreeMap(group1, x, y, width1, height);
        createTreeMap(group2, x + width1, y, width - width1, height);
      } else {
        // Split vertically
        const height1 = Math.floor(height * ratio);
        createTreeMap(group1, x, y, width, height1);
        createTreeMap(group2, x, y + height1, width, height - height1);
      }
    }
    
    // Temporary storage for all rectangles
    const allRectangles = [];
    
    // Draw a rectangle for an item
    function drawRect(item, x, y, width, height) {
      // Store rectangle info for click detection
      allRectangles.push({item, x, y, width, height});
      
      // Draw rectangle with anti-aliasing
      ctx.fillStyle = item.color;
      ctx.beginPath();
      ctx.rect(x, y, width, height);
      ctx.fill();
      
      // Draw border with crisp edges
      ctx.strokeStyle = treemapPalette.border;
      ctx.lineWidth = 1; // Thinner lines look better with high DPI
      ctx.beginPath();
      ctx.rect(x, y, width, height);
      ctx.stroke();
      
      // Prepare label text - start with just the name
      let text = `${item.label}`;
      
      // Add percentage for Cash
      if (item.label === 'Cash') {
        text = `${item.label}\n(${item.percentage.toFixed(1)}%)`;
      }
      
      // Add gain/loss information for categories or tickers
      if (item.category || item.ticker) {
        // Get the gain percentage
        let gainPct = item.gainPct || 0;
        
        // Format gain/loss percentage with proper sign
        const formattedGainPct = gainPct >= 0 ? 
          `${gainPct.toFixed(2)}%` : 
          `-${Math.abs(gainPct).toFixed(2)}%`;
        
        // Add category percentage on its own line with parentheses and gain/loss on next line
        text = `${item.label}\n(${item.percentage.toFixed(2)}%)\n${formattedGainPct}`;
        
        // If this is a category view (not a ticker view), add ticker symbols
        if (item.category && !item.isSingleTickerView) {
          // Remove the pre-bal basis calculation for categories
          const tickersArray = item.category.tickers.map(t => t.symbol);

          // Set font before measuring to ensure accurate width calculations
          const area = width * height;
          const baseFontSize = (Math.sqrt(area) / 8) * fontSizeMultiplier;
          const maxFontSize = 18 * fontSizeMultiplier;
          const fontSize = Math.max(10, Math.min(maxFontSize, baseFontSize));
          ctx.font = `${fontSize}px Arial`;

          // Calculate available width (leave some padding)
          const availableWidth = width * 0.9;

          // Build ticker lines that fit within the width
          const tickerLines = [];
          let currentLine = '';

          for (let i = 0; i < tickersArray.length; i++) {
            const ticker = tickersArray[i];
            const separator = i === 0 ? '' : ', ';
            const testText = currentLine + separator + ticker;

            // Measure the width of the test text
            const textWidth = ctx.measureText(testText).width;

            if (textWidth <= availableWidth || currentLine === '') {
              // Fits on current line
              currentLine = testText;
            } else {
              // Doesn't fit, push current line and start new one
              tickerLines.push(currentLine);
              currentLine = ticker;
            }
          }

          // Don't forget the last line
          if (currentLine) {
            tickerLines.push(currentLine);
          }

          // Calculate maximum number of lines we can fit
          const lineHeight = fontSize * 1.2;
          const existingLines = text.split('\n').length;
          const usedHeight = existingLines * lineHeight;
          const remainingHeight = height - usedHeight;
          const maxTickerLines = Math.floor(remainingHeight / lineHeight);

          // Add ticker lines (up to what fits)
          const linesToShow = tickerLines.slice(0, Math.max(1, maxTickerLines));

          // Add ellipsis if we couldn't show all lines
          if (linesToShow.length < tickerLines.length) {
            const lastLineIndex = linesToShow.length - 1;
            linesToShow[lastLineIndex] = linesToShow[lastLineIndex] + '...';
          }

          // Add all ticker lines to text
          linesToShow.forEach(line => {
            text += `\n${line}`;
          });
        } else if (item.ticker) {
          // For ticker view in subcategory, update text to include percentage on its own line with parentheses
          text = `${item.label}\n(${item.percentage.toFixed(2)}%)\n${formattedGainPct}`;
          
          // For ticker view, do not append company name
        }
      }
      
      // Use font size proportional to area, with bounds
      const area = width * height;
      const baseFontSize = (Math.sqrt(area) / 8) * fontSizeMultiplier;
      const maxFontSize = 18 * fontSizeMultiplier;
      const fontSize = Math.max(10, Math.min(maxFontSize, baseFontSize));
      
      // Apply font with proper anti-aliasing
      ctx.font = `${fontSize}px Arial`;
      
      const textColor = getTextToneForFill(item.color);
      ctx.fillStyle = textColor;
      ctx.shadowColor = textColor === treemapPalette.textLight
        ? treemapPalette.shadowLight
        : treemapPalette.shadowDark;
      
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // Apply text shadow for better readability
      ctx.shadowBlur = 2;
      
      // Draw text if there's enough space
      if (width > 40 && height > 20) {
        // Check if there are multiple lines
        if (text.includes('\n')) {
          const lines = text.split('\n');
          const lineHeight = fontSize * 1.2;
          const yOffset = (lines.length - 1) * lineHeight / 2;
          
          lines.forEach((line, index) => {
            const yPos = y + height/2 - yOffset + index * lineHeight;
            ctx.fillText(line, x + width/2, yPos);
          });
        } else {
          ctx.fillText(text, x + width/2, y + height/2);
        }
      }

      // Draw gold star for exceptional gains (>9%)
      if (item.gainPct && item.gainPct > 9) {
        const starSize = Math.min(fontSize * 1.2, 20); // Size based on font but capped
        const starPadding = 4; // Padding from corner

        ctx.save();
        ctx.fillStyle = '#FFD700'; // Gold color
        ctx.font = `bold ${starSize}px Arial`;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'top';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
        ctx.shadowBlur = 3;

        ctx.fillText('★', x + width - starPadding, y + starPadding);
        ctx.restore();
      }

      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
    }
    
    // Start the tree map algorithm
    createTreeMap(items, padding, padding, canvasWidth - padding*2, canvasHeight - padding*2);

    logger.debug('Updated rectangles:', allRectangles.length);
    
    // Define the click handler directly inside this useEffect
    function handleCanvasClick(event) {
      const x = event.clientX - canvas.getBoundingClientRect().left;
      const y = event.clientY - canvas.getBoundingClientRect().top;
      
      logger.debug('Canvas clicked, checking rectangles:', allRectangles.length);
      
      // Check if click is inside any rectangle
      const clickedRect = allRectangles.find(r => 
        x >= r.x && x <= r.x + r.width && 
        y >= r.y && y <= r.y + r.height
      );
      
      if (clickedRect && clickedRect.item) {
        logger.debug('Clicked on item:', clickedRect.item.label);

        // Check if clicked item is a ticker with a trade ID
        if (clickedRect.item.ticker) {
          const ticker = clickedRect.item.ticker;
          logger.debug('Clicked ticker object:', ticker);
          logger.debug('Ticker keys:', Object.keys(ticker));
          logger.debug('Ticker accounts:', ticker.accounts);

          const tradeId = ticker.current_trade_id || ticker.trade_id;

          if (tradeId) {
            logger.debug('Navigating to trade details:', { symbol: ticker.symbol, tradeId });
            navigate(`/trades/${tradeId}`);
            return;
          } else {
            logger.debug('Ticker has no trade ID, skipping navigation. ticker.current_trade_id:', ticker.current_trade_id, 'ticker.trade_id:', ticker.trade_id);
          }
        }

        if (viewMode === 'ticker') return;

        // If we're in the main view (no selected category)
        if (!selectedCategory) {
          // Only handle clicks on categories (items with 'category' property)
          if (clickedRect.item.category) {
            logger.debug('Selecting category:', clickedRect.item.label);
            setSelectedCategoryLabel(clickedRect.item.label);
          }
        }
        // If we're already in a category view
        else {
          // If Back button is clicked, it handles going back, not this handler

          // If we click on another category in main view (item with 'category' and no 'ticker')
          if (clickedRect.item.category && !clickedRect.item.ticker) {
            logger.debug('Switching to category:', clickedRect.item.label);
            setSelectedCategoryLabel(clickedRect.item.label);
          }
        }
      }
    }
    
    // Add click event listener
    canvas.addEventListener('click', handleCanvasClick);
    
    // Return cleanup function that properly removes event listener
    return () => {
      if (canvas) {
        canvas.removeEventListener('click', handleCanvasClick);
      }
    };
  }, [show, tickers, cash, selectedCategory, useToday, viewMode, treemapPalette, fontSizeMultiplier, getTextToneForFill, isDark, navigate]);

  const headerTitle = (
    <h5 className="mb-0">
      {selectedCategory ? (
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={handleBackToMain}
            className="me-2"
          >
            <i className="fas fa-arrow-left"></i> Back
          </Button>
          {selectedCategory.label} Composition
        </div>
      ) : (
        <div className="d-flex align-items-center">
          TreeMap
        </div>
      )}
    </h5>
  );

  const gainSummary = tickers && tickers.length > 0 && (
    <div className="fs-5 fw-bold ms-auto">
      <span className="ms-2">
        Pre-Bal: {(() => {
          // If we have an account-level previous balance from backend, use it to match stats card
          if (previousBalance !== null && previousBalance !== undefined) {
            const holdingsCurrent = tickers.reduce((sum, ticker) => sum + (ticker.current_value || 0), 0);
            const currentTotal = holdingsCurrent + (cash || 0);
            const gainAmount = currentTotal - previousBalance;
            const gainPct = previousBalance > 0 ? (gainAmount / previousBalance) * 100 : 0;

            if (gainAmount >= 0) {
              return (
                <span className="text-success">
                  ${Math.round(gainAmount)} ({gainPct.toFixed(2)}%)
                </span>
              );
            }
            return (
              <span className="text-danger">
                -${Math.abs(Math.round(gainAmount))} (-{Math.abs(gainPct).toFixed(2)}%)
              </span>
            );
          }

          // No fallback calculation - show N/A when previous balance unavailable
          return (
            <span
              className="text-muted"
              title="Previous day balance not available. Balance snapshots are recorded at market close."
            >
              N/A
            </span>
          );
        })()}
      </span>
    </div>
  );

  const controls = (
    <Form className="d-flex align-items-center gap-3 flex-wrap">
      <Form.Group controlId="gain-select" className="d-flex align-items-center mb-0 gap-0">
        <Form.Select
          size="sm"
          value={useToday ? 'today' : 'total'}
          onChange={(e) => handleGainBasisChange(e.target.value === 'today')}
        >
          <option value="today">Today's Gain%</option>
          <option value="total">Total Gain%</option>
        </Form.Select>
      </Form.Group>
      <Form.Group controlId="view-select" className="d-flex align-items-center mb-0 gap-2">
        <Form.Select
          size="sm"
          value={viewMode}
          onChange={(e) => handleViewModeChange(e.target.value)}
        >
          <option value="category">Category</option>
          <option value="ticker">Ticker</option>
        </Form.Select>
      </Form.Group>
    </Form>
  );

  const colorLegend = (
    <Dropdown align="end">
      <Dropdown.Toggle variant="outline-secondary" size="sm" id="color-legend-dropdown">
        <i className="fas fa-palette me-1"></i> Legend
      </Dropdown.Toggle>
      <Dropdown.Menu style={{ minWidth: '220px', maxHeight: '600px', overflowY: 'auto' }}>
        <Dropdown.ItemText>
          <div className="d-flex flex-column gap-2">
            <div className="fw-bold text-success mb-1">Gains</div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: treemapPalette.gain[0].color,
                border: '1px solid #ddd',
                borderRadius: '3px'
              }}></div>
              <span className="small text-body">0% to 3%</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: treemapPalette.gain[1].color,
                border: '1px solid #ddd',
                borderRadius: '3px'
              }}></div>
              <span className="small text-body">3% to 5%</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: treemapPalette.gain[2].color,
                border: '1px solid #ddd',
                borderRadius: '3px'
              }}></div>
              <span className="small text-body">5% to 9%</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: treemapPalette.gain[3].color,
                border: '1px solid #ddd',
                borderRadius: '3px'
              }}></div>
              <span className="small text-body fw-bold">9% and above</span>
            </div>

            <div className="fw-bold text-danger mt-2 mb-1">Losses</div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: treemapPalette.loss[0].color,
                border: '1px solid #ddd',
                borderRadius: '3px'
              }}></div>
              <span className="small text-body">0% to -3%</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: treemapPalette.loss[1].color,
                border: '1px solid #ddd',
                borderRadius: '3px'
              }}></div>
              <span className="small text-body">-3% to -5%</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: treemapPalette.loss[2].color,
                border: '1px solid #ddd',
                borderRadius: '3px'
              }}></div>
              <span className="small text-body">-5% to -9%</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: treemapPalette.loss[3].color,
                border: '1px solid #ddd',
                borderRadius: '3px'
              }}></div>
              <span className="small text-body fw-bold">-9% and below</span>
            </div>
          </div>
        </Dropdown.ItemText>
      </Dropdown.Menu>
    </Dropdown>
  );

  const shouldShowOverlayBack = Boolean(selectedCategory) && hideTitle;
  const canvasSection = (
    <div className={inline ? 'treemap-inline-canvas' : ''} style={{
      width: '100%',
      height: inline ? '24vh' : '80vh', // Inline view is shorter to align with table
      maxHeight: inline ? '260px' : '700px',
      position: 'relative',
      backgroundColor: treemapPalette.background
    }}>
      {shouldShowOverlayBack && (
        <button
          type="button"
          className="treemap-back-button"
          onClick={handleBackToMain}
        >
          Back
        </button>
      )}
      <canvas 
        ref={chartRef} 
        style={{
          width: '100%',
          height: '100%',
          display: 'block', // Remove inline-block spacing issues
          cursor: 'pointer' // Change cursor to pointer
        }}
      />
    </div>
  );

  if (inline) {
    if (!show) return null;

    return (
      <div className="treemap-inline card">
        <div className="card-body">
          {(!hideTitle || !hideControls || !hideLegend || !hidePreBal) && (
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3 flex-wrap w-100">
                {!hideTitle && headerTitle}
                {!hideControls && controls}
                {!hideLegend && colorLegend}
                {!hidePreBal && gainSummary}
              </div>
            </div>
          )}
          <div>
            {canvasSection}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop={true}
      keyboard={true}
      size="xl"
      dialogClassName="treemap-modal"
    >
      <Modal.Header closeButton>
        <div className="d-flex align-items-center gap-3 flex-wrap w-100">
          {!hideTitle && headerTitle}
          {!hideControls && controls}
          {!hideLegend && colorLegend}
          {!hidePreBal && gainSummary}
        </div>
      </Modal.Header>
      <Modal.Body className="p-0">
        {canvasSection}
      </Modal.Body>
    </Modal>
  );
}

export default TreeMapModal; 
